import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
// AI icon uses /images/vs-spinner.png
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { AiSparkle } from '@/components/AiSparkle'
import { Form } from '@/components/ui/form'
import { MasterRunbookForm } from '../form'
import { useCreateMasterRunbook, useUpdateMasterRunbook } from '../hooks'
import { masterRunbookSchema, type MasterRunbookFormData } from '../schema'
import { calculateRunbookTimes } from '@/features/objects/utils/calculateRunbookTimes'
import { masterRunbooksApi } from '../api'
import type { MasterRunbook } from '../api'
import { AiGenerateRunbookDialog } from './AiGenerateRunbookDialog'
import type { RunbookTask } from '@/types/entities'

interface MasterRunbookFormDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
    masterRunbook: MasterRunbook | null
}

export function MasterRunbookFormDialog({ open, onOpenChange, masterRunbook }: MasterRunbookFormDialogProps) {
    const { t } = useTranslation()
    const isEditing = !!masterRunbook
    const createMasterRunbook = useCreateMasterRunbook()
    const updateMasterRunbook = useUpdateMasterRunbook()

    const form = useForm<MasterRunbookFormData>({
        resolver: zodResolver(masterRunbookSchema),
        mode: 'onChange',
        defaultValues: {
            description: '',
            information: '',
            icon: null,
            tasks: [],
        },
    })

    // AI generation inputs persisted alongside the runbook (not form-edited fields)
    const [aiTechnologies, setAiTechnologies] = useState<any[]>([])
    const [aiGenerationContext, setAiGenerationContext] = useState('')
    const [aiGeneratedName, setAiGeneratedName] = useState('')

    // Reset form when dialog opens/closes or masterRunbook changes
    useEffect(() => {
        if (open) {
            if (masterRunbook) {
                // Load master runbook with tasks
                masterRunbooksApi.get(masterRunbook.id)
                    .then((response) => {
                        const data = response.data?.data || response.data
                        // Calculate TaskOffset and times for display
                        const tasksWithTimes = calculateRunbookTimes(data.tasks || [])
                        form.reset({
                            description: data.description || masterRunbook.description,
                            information: data.information || masterRunbook.information || '',
                            icon: data.iconsId != null ? String(data.iconsId) : (masterRunbook.iconsId != null ? String(masterRunbook.iconsId) : null),
                            tasks: tasksWithTimes,
                        })
                        setAiTechnologies(Array.isArray(data.technologies) ? data.technologies : [])
                        setAiGenerationContext(data.generationContext || '')
                        setAiGeneratedName(data.generatedName || '')
                    })
                    .catch(() => {
                        // Fallback if tasks can't be loaded
                        form.reset({
                            description: masterRunbook.description,
                            information: masterRunbook.information || '',
                            icon: masterRunbook.iconsId != null ? String(masterRunbook.iconsId) : null,
                            tasks: [],
                        })
                        setAiTechnologies([])
                        setAiGenerationContext('')
                        setAiGeneratedName('')
                    })
            } else {
                form.reset({
                    description: '',
                    information: '',
                    icon: null,
                    tasks: [],
                })
                setAiTechnologies([])
                setAiGenerationContext('')
                setAiGeneratedName('')
            }
        }
    }, [open, masterRunbook, form])

    const [aiCode, setAiCode] = useState<Record<string, any> | null>(null)

    const onSubmit = async (data: MasterRunbookFormData) => {
        try {
            // Ensure tasks have calculated TaskOffset before submitting
            if (data.tasks && data.tasks.length > 0) {
                const { calculateRunbookTimes } = await import('@/features/objects/utils/calculateRunbookTimes')
                data.tasks = calculateRunbookTimes(data.tasks)
            }

            // Persist the AI generation inputs (technologies + context) with the runbook
            const { icon, ...payloadData } = data
            const payload = {
                ...payloadData,
                iconsId: icon ? Number(icon) : null,
                technologies: aiTechnologies,
                generationContext: aiGenerationContext,
                generatedName: aiGeneratedName,
                ...(aiCode ? { code: aiCode } : {}),
            }

            if (isEditing && masterRunbook) {
                await updateMasterRunbook.mutateAsync({
                    id: masterRunbook.id,
                    data: payload,
                })
            } else {
                await createMasterRunbook.mutateAsync(payload)
            }
            onOpenChange(false)
        } catch (error) {
            // Error handling is done in the hooks
        }
    }

    const isLoading = createMasterRunbook.isPending || updateMasterRunbook.isPending
    const [aiDialogOpen, setAiDialogOpen] = useState(false)

    const handleAiGenerated = (
        generatedName: string,
        tasks: RunbookTask[],
        meta?: { technologies: any[]; generationContext: string; code?: Record<string, any> }
    ) => {
        const tasksWithTimes = calculateRunbookTimes(tasks)
        // Do NOT overwrite the user-given Name (description). The auto-built detailed
        // "RUNBOOK FOR" string is kept separately as the generated name.
        setAiGeneratedName(generatedName || '')
        form.setValue('tasks', tasksWithTimes)
        if (meta) {
            setAiTechnologies(meta.technologies || [])
            setAiGenerationContext(meta.generationContext || '')
            // Scripts the generator wrote for the automated tasks. Held until save, because
            // they are stored against a runbook id that does not exist until then.
            setAiCode(meta.code || null)
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="w-[95vw] sm:max-w-[1600px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <div className="flex items-center justify-between">
                        <DialogTitle>
                            {isEditing ? (masterRunbook?.description || t('masterRunbooks.editMasterRunbook')) : t('masterRunbooks.createMasterRunbook')}
                        </DialogTitle>
                        <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setAiDialogOpen(true)}
                            className="gap-2 mr-8"
                        >
                            <AiSparkle className="h-5 w-5 text-blue-500" />
                            {isEditing
                                ? t('masterRunbooks.aiRegenerate', 'AI Regenerate')
                                : t('masterRunbooks.aiGenerate', 'AI Generate')}
                        </Button>
                    </div>
                    <DialogDescription>
                        {isEditing
                            ? t('masterRunbooks.editMasterRunbookDescription')
                            : t('masterRunbooks.createMasterRunbookDescription')}
                    </DialogDescription>
                </DialogHeader>

                <AiGenerateRunbookDialog
                    open={aiDialogOpen}
                    onOpenChange={setAiDialogOpen}
                    onGenerated={handleAiGenerated}
                    initialTechnologies={aiTechnologies}
                    initialContext={aiGenerationContext}
                />

                {/* Saved AI generation inputs — shown so the user knows what produced
                    this runbook and can regenerate via the AI Regenerate button */}
                {(aiTechnologies.length > 0 || aiGenerationContext || aiGeneratedName) && (
                    <div className="rounded-lg border bg-muted/30 p-3 space-y-2">
                        <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
                            {t('masterRunbooks.generatedFrom', 'Generated from')}
                        </p>
                        {aiGeneratedName && (
                            <p className="text-sm text-foreground">
                                <span className="text-xs font-semibold text-muted-foreground">{t('masterRunbooks.detailedName', 'Detailed name')}: </span>
                                {aiGeneratedName}
                            </p>
                        )}
                        {aiTechnologies.length > 0 && (
                            <div className="flex flex-wrap gap-1.5">
                                {aiTechnologies.map((tech: any, i: number) => (
                                    <span key={i} className="inline-flex items-center rounded-full bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 text-xs">
                                        {tech.category ? `${tech.category}: ` : ''}{tech.name}
                                    </span>
                                ))}
                            </div>
                        )}
                        {aiGenerationContext && (
                            <p className="text-sm text-foreground">
                                <span className="text-xs font-semibold text-muted-foreground">{t('masterRunbooks.additionalContext', 'Additional context')}: </span>
                                {aiGenerationContext}
                            </p>
                        )}
                    </div>
                )}

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                        <MasterRunbookForm key={masterRunbook?.id || 'new'} form={form} masterRunbookId={masterRunbook?.id ?? null} aiCode={aiCode} />

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                                disabled={isLoading}
                            >
                                {t('common.cancel')}
                            </Button>
                            <Button type="submit" disabled={isLoading}>
                                {isLoading
                                    ? t('common.saving')
                                    : isEditing
                                    ? t('common.save')
                                    : t('common.create')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
